import SHA256 from "crypto-js/sha256";

import { getSourceUrlWithoutCredentials } from "./source-url-observability";
import { getYoutubeVideoId } from "./youtube-url";

export enum ContentIngestionStage {
  ValidateUrl = "validate-url",
  FetchSource = "fetch-source",
  RequestYouTubeTranscript = "request-youtube-transcript",
  AwaitYouTubeTranscript = "await-youtube-transcript",
  FinalizeSource = "finalize-source",
}

export enum ContentIngestionErrorCode {
  InvalidUrl = "invalid-url",
  Network = "network",
  Http = "http",
  InvalidResponse = "invalid-response",
  SourceRejected = "source-rejected",
  Timeout = "timeout",
  Listener = "listener",
  EmptyContent = "empty-content",
  Unknown = "unknown",
}

export type ContentIngestionTranslate = (
  key: string,
  fallback: string,
  parameters?: Record<string, string | number>
) => string;

const useFallbackTranslation: ContentIngestionTranslate = (_key, fallback) => fallback;

/**
 * Learner-facing ingestion copy travels as a translation descriptor and is
 * resolved with the caller's `t` where it is displayed, because these errors
 * are created far from any React translation context. The property names
 * `translationKey` and `defaultTranslation` are load-bearing: the locale
 * coverage test registers every object literal carrying both, so each key must
 * exist in every `src/i18n/*.json` file. `serverProvidedText` is backend copy
 * without a stable error code; it is shown unchanged.
 */
export type ContentIngestionUserMessage =
  | { translationKey: string; defaultTranslation: string }
  | { serverProvidedText: string };

export const CONTENT_INGESTION_USER_MESSAGES = {
  generic: {
    translationKey: "content_ingestion_error.generic",
    defaultTranslation: "we couldn’t get data from this source. please check the link and try again.",
  },
  offline: {
    translationKey: "content_ingestion_error.offline",
    defaultTranslation: "your internet connection was interrupted. reconnect, then try this link again.",
  },
  connectionInterrupted: {
    translationKey: "content_ingestion_error.connection_interrupted",
    defaultTranslation: "the connection was interrupted while getting this source. please try again.",
  },
  timeout: {
    translationKey: "content_ingestion_error.timeout",
    defaultTranslation: "this source is taking longer than expected to process. please try again.",
  },
  serviceUnavailable: {
    translationKey: "content_ingestion_error.service_unavailable",
    defaultTranslation: "the content service is temporarily unavailable. please wait a moment and try again.",
  },
  invalidUrl: {
    translationKey: "content_ingestion_error.invalid_url",
    defaultTranslation: "enter a valid public webpage, PDF, image, or YouTube URL.",
  },
  uploadMediaFileDirectly: {
    translationKey: "content_ingestion_error.upload_media_file_directly",
    defaultTranslation: "download this audio, video, or Anki file and upload it directly to Jungle.",
  },
  localFileUrl: {
    translationKey: "content_ingestion_error.local_file_url",
    defaultTranslation:
      "You can't generate questions from a local file PDF opened from your computer. Please use the upload PDF button below to upload the PDF you want to generate questions for.",
  },
  pdfLinkUnavailable: {
    translationKey: "content_ingestion_error.pdf_link_unavailable",
    defaultTranslation:
      "We couldn't access this PDF link because it is private, expired, or no longer available. Download the PDF and upload it directly to Jungle.",
  },
  pdfLinkNotDirectFile: {
    translationKey: "content_ingestion_error.pdf_link_not_direct_file",
    defaultTranslation:
      "Failed to get text from the URL you provided due to privacy restrictions or because the URL does not link directly to a file. Please download the file and upload it to Jungle.",
  },
  privateGoogleDocument: {
    translationKey: "content_ingestion_error.private_google_document",
    defaultTranslation:
      "This Google Document is private. Use Share > General access > \"Anyone with the link\", or keep it private: choose File > Download > PDF Document (.pdf) and upload the PDF here.",
  },
  privateGoogleSlidesPresentation: {
    translationKey: "content_ingestion_error.private_google_slides_presentation",
    defaultTranslation:
      "This Google Slides presentation is private. Use Share > General access > \"Anyone with the link\", or keep it private: choose File > Download > PDF Document (.pdf) and upload the PDF here.",
  },
  webpagePrivacyRestricted: {
    translationKey: "content_ingestion_error.webpage_privacy_restricted",
    defaultTranslation:
      "Failed to get text from the URL you provided because this webpage has privacy restrictions from external sites.",
  },
  googleDriveFileTooLarge: {
    translationKey: "content_ingestion_error.google_drive_file_too_large",
    defaultTranslation: "The file is too large. Download it as PDF first, then upload the PDF.",
  },
  googleDocConversionFailed: {
    translationKey: "content_ingestion_error.google_doc_conversion_failed",
    defaultTranslation:
      "An error occurred: Failed to convert Google Doc to PDF. Please check the document or try again later. You can also try downloading the file as a PDF and uploading it to Jungle",
  },
  webpageUnavailable: {
    translationKey: "content_ingestion_error.webpage_unavailable",
    defaultTranslation:
      "We couldn't access this webpage because it is no longer available. Check the URL or try another public source.",
  },
  webpageWithoutReadableContent: {
    translationKey: "content_ingestion_error.webpage_without_readable_content",
    defaultTranslation: "we couldn’t find readable content on this webpage. try another public URL or upload a file.",
  },
  youtubeTranscriptTimeout: {
    translationKey: "content_ingestion_error.youtube_transcript_timeout",
    defaultTranslation: "This video's transcript is taking longer than expected. Please try again in a moment.",
  },
  youtubeTranscriptListenerFailed: {
    translationKey: "content_ingestion_error.youtube_transcript_listener_failed",
    defaultTranslation: "We couldn't finish loading this video's transcript. Please try again or use another video.",
  },
  youtubeTranscriptUnavailable: {
    translationKey: "content_ingestion_error.youtube_transcript_unavailable",
    defaultTranslation:
      "Cards could not be generated for this YouTube video because it does not have a transcript available. Please try another video.",
  },
} satisfies Record<string, ContentIngestionUserMessage>;

export function translateContentIngestionUserMessage(
  userMessage: ContentIngestionUserMessage,
  translate: ContentIngestionTranslate = useFallbackTranslation
): string {
  if ("serverProvidedText" in userMessage) return userMessage.serverProvidedText;
  return translate(userMessage.translationKey, userMessage.defaultTranslation);
}

interface ContentIngestionErrorOptions {
  message: string;
  stage: ContentIngestionStage;
  code: ContentIngestionErrorCode;
  retryable: boolean;
  requestAttempts?: number;
  httpStatus?: number;
  cfRayId?: string;
  responseText?: string;
  userMessage?: ContentIngestionUserMessage;
  cause?: unknown;
}

export class ContentIngestionError extends Error {
  readonly stage: ContentIngestionStage;
  readonly code: ContentIngestionErrorCode;
  readonly retryable: boolean;
  readonly requestAttempts: number;
  readonly httpStatus?: number;
  readonly cfRayId?: string;
  readonly responseText?: string;
  readonly userMessage?: ContentIngestionUserMessage;
  readonly cause?: unknown;

  constructor({
    message,
    stage,
    code,
    retryable,
    requestAttempts = 1,
    httpStatus,
    cfRayId,
    responseText,
    userMessage,
    cause,
  }: ContentIngestionErrorOptions) {
    super(message);
    this.name = "ContentIngestionError";
    this.stage = stage;
    this.code = code;
    this.retryable = retryable;
    this.requestAttempts = requestAttempts;
    this.httpStatus = httpStatus;
    this.cfRayId = cfRayId;
    if (responseText !== undefined) {
      Object.defineProperty(this, "responseText", {
        value: responseText,
        enumerable: false,
      });
    }
    this.userMessage = userMessage;
    this.cause = cause;
  }
}

type SleepImplementation = (milliseconds: number) => Promise<void>;

const defaultSleep: SleepImplementation = async (milliseconds) => {
  await new Promise<void>((resolve) => globalThis.setTimeout(resolve, milliseconds));
};

export async function requestContentEndpoint<T>({
  url,
  body,
  stage = ContentIngestionStage.FetchSource,
  fetchImpl = fetch,
  sleepMs = defaultSleep,
  maxAttempts = 3,
  timeoutMs = 30_000,
}: {
  url: string;
  body: Record<string, unknown>;
  stage?: ContentIngestionStage;
  fetchImpl?: typeof fetch;
  sleepMs?: SleepImplementation;
  maxAttempts?: number;
  timeoutMs?: number;
}): Promise<T> {
  let response: Response | null = null;
  let responseText = "";
  let requestAttempts = 0;
  let lastRetryableHttpResponse: Response | null = null;
  let lastRetryableHttpResponseText = "";
  let lastNetworkError: unknown;
  let didLastAttemptTimeOut = false;

  // URL fetches are duplicate-safe, while the YouTube endpoint locks work by
  // transcript status. Retry transport failures plus temporary HTTP failures;
  // never repeat permanent source rejections such as 403, 404, or 415.
  for (let attemptNumber = 1; attemptNumber <= maxAttempts; attemptNumber += 1) {
    requestAttempts = attemptNumber;
    const abortController = new AbortController();
    const timeoutId = globalThis.setTimeout(() => abortController.abort(), timeoutMs);
    try {
      response = await fetchImpl(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: abortController.signal,
      });
      responseText = await response.text();
      const shouldRetryHttpResponse =
        attemptNumber < maxAttempts && (response.status === 429 || response.status >= 500);
      if (shouldRetryHttpResponse) {
        lastRetryableHttpResponse = response;
        lastRetryableHttpResponseText = responseText;
        response = null;
        responseText = "";
        await sleepMs(400 * attemptNumber);
        continue;
      }
      break;
    } catch (error) {
      response = null;
      responseText = "";
      lastNetworkError = error;
      didLastAttemptTimeOut = abortController.signal.aborted;
      if (attemptNumber < maxAttempts) {
        await sleepMs(400 * attemptNumber);
      }
    } finally {
      globalThis.clearTimeout(timeoutId);
    }
  }

  if (!response && lastRetryableHttpResponse) {
    response = lastRetryableHttpResponse;
    responseText = lastRetryableHttpResponseText;
  }

  if (!response) {
    throw new ContentIngestionError({
      message: didLastAttemptTimeOut ? "Content endpoint request timed out" : "Content endpoint request failed",
      stage,
      code: didLastAttemptTimeOut ? ContentIngestionErrorCode.Timeout : ContentIngestionErrorCode.Network,
      retryable: true,
      requestAttempts,
      cause: lastNetworkError,
    });
  }

  if (!response.ok) {
    throw new ContentIngestionError({
      message: "Content endpoint returned an HTTP error",
      stage,
      code: ContentIngestionErrorCode.Http,
      retryable: response.status === 429 || response.status >= 500,
      requestAttempts,
      httpStatus: response.status,
      cfRayId: response.headers?.get("cf-ray") || undefined,
      responseText: responseText.slice(0, 1_000),
    });
  }

  try {
    return JSON.parse(responseText) as T;
  } catch (error) {
    throw new ContentIngestionError({
      message: "Content endpoint returned invalid JSON",
      stage,
      code: ContentIngestionErrorCode.InvalidResponse,
      retryable: true,
      requestAttempts,
      cause: error,
    });
  }
}

export function normalizeContentUrl(rawUrl: string): string {
  const trimmedUrl = rawUrl?.trim();
  if (!trimmedUrl) {
    throw new ContentIngestionError({
      message: "Content URL is empty",
      stage: ContentIngestionStage.ValidateUrl,
      code: ContentIngestionErrorCode.InvalidUrl,
      retryable: false,
    });
  }

  const hasExplicitScheme = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmedUrl);
  const candidateUrl = hasExplicitScheme ? trimmedUrl : `https://${trimmedUrl}`;
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(candidateUrl);
  } catch (error) {
    throw new ContentIngestionError({
      message: "Content URL is malformed",
      stage: ContentIngestionStage.ValidateUrl,
      code: ContentIngestionErrorCode.InvalidUrl,
      retryable: false,
      cause: error,
    });
  }

  const hostnameLabels = parsedUrl.hostname.split(".");
  const hasInvalidHostname =
    parsedUrl.hostname.includes("%") ||
    hostnameLabels.some((label) => !label || label.startsWith("-") || label.endsWith("-"));
  if (
    (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") ||
    !parsedUrl.hostname ||
    hasInvalidHostname ||
    parsedUrl.username ||
    parsedUrl.password
  ) {
    throw new ContentIngestionError({
      message: "Content URL must be a public HTTP or HTTPS URL",
      stage: ContentIngestionStage.ValidateUrl,
      code: ContentIngestionErrorCode.InvalidUrl,
      retryable: false,
    });
  }

  return parsedUrl.toString();
}

export function getSafeUrlMetadata(rawUrl: string) {
  try {
    const trimmedUrl = rawUrl?.trim();
    const parsedUrl = new URL(/^[a-z][a-z\d+.-]*:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`);
    if (
      (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") ||
      !parsedUrl.hostname ||
      parsedUrl.hostname.includes("%")
    ) {
      throw new Error("Unsafe URL metadata");
    }
    const finalPathSegment = parsedUrl.pathname.split("/").pop() || "";
    const sourceExtension = finalPathSegment.includes(".")
      ? finalPathSegment.split(".").pop()?.toLowerCase() || ""
      : "";
    const sourceYouTubeVideoId = getYoutubeVideoId(parsedUrl.toString());
    const querylessSourceLocator = `${parsedUrl.protocol}//${parsedUrl.host}${parsedUrl.pathname}`;
    return {
      sourceProtocol: parsedUrl.protocol,
      sourceHostname: parsedUrl.hostname.toLowerCase(),
      sourceExtension,
      sourceUrlWithoutCredentialsQueryOrFragment: querylessSourceLocator,
      sourceUrlWithoutCredentials: getSourceUrlWithoutCredentials(parsedUrl.toString()),
      sourceYouTubeVideoId,
      // These names deliberately avoid "url": organization-level Sentry
      // scrubbing can remove URL-shaped fields even after credentials are gone.
      source_path: parsedUrl.pathname || "/",
      source_locator_hash: SHA256(querylessSourceLocator).toString(),
      hasQuery: parsedUrl.search.length > 0,
      hasFragment: parsedUrl.hash.length > 0,
    };
  } catch {
    return {
      sourceProtocol: "unknown",
      sourceHostname: "unknown",
      sourceExtension: "",
      sourceUrlWithoutCredentialsQueryOrFragment: null,
      sourceUrlWithoutCredentials: null,
      sourceYouTubeVideoId: null,
      source_path: null,
      source_locator_hash: null,
      hasQuery: false,
      hasFragment: false,
    };
  }
}

export function buildContentIngestionFailureTelemetry({
  error,
  attemptId,
  sourceUrl,
  contentMediumType,
  isOnline,
}: {
  error: ContentIngestionError;
  attemptId: string;
  sourceUrl: string;
  contentMediumType: string | null;
  isOnline: boolean;
}) {
  return {
    outcome: "failed",
    stage: error.stage,
    errorCode: error.code,
    retryable: error.retryable,
    requestAttempts: error.requestAttempts,
    ...(error.httpStatus ? { httpStatus: error.httpStatus } : {}),
    cfRayId: error.cfRayId || null,
    attemptId,
    contentMediumType: contentMediumType || "unknown",
    isOnline,
    ...getSafeUrlMetadata(sourceUrl),
  };
}

export function getContentIngestionUserMessage(
  error: unknown,
  isOnline: boolean,
  translate: ContentIngestionTranslate = useFallbackTranslation
): string {
  return translateContentIngestionUserMessage(getContentIngestionUserMessageDescriptor(error, isOnline), translate);
}

function getContentIngestionUserMessageDescriptor(error: unknown, isOnline: boolean): ContentIngestionUserMessage {
  if (!(error instanceof ContentIngestionError)) {
    return CONTENT_INGESTION_USER_MESSAGES.generic;
  }
  if (error.userMessage) return error.userMessage;
  if (error.code === ContentIngestionErrorCode.Network && !isOnline) {
    return CONTENT_INGESTION_USER_MESSAGES.offline;
  }
  if (error.code === ContentIngestionErrorCode.Network) {
    return CONTENT_INGESTION_USER_MESSAGES.connectionInterrupted;
  }
  if (error.code === ContentIngestionErrorCode.Timeout) {
    return CONTENT_INGESTION_USER_MESSAGES.timeout;
  }
  if (error.code === ContentIngestionErrorCode.Http && error.httpStatus && error.httpStatus >= 500) {
    return CONTENT_INGESTION_USER_MESSAGES.serviceUnavailable;
  }
  if (error.code === ContentIngestionErrorCode.InvalidUrl) {
    return CONTENT_INGESTION_USER_MESSAGES.invalidUrl;
  }
  return CONTENT_INGESTION_USER_MESSAGES.generic;
}

export function normalizeContentIngestionError(error: unknown): ContentIngestionError {
  if (error instanceof ContentIngestionError) return error;
  return new ContentIngestionError({
    message: error instanceof Error ? error.message : "Unexpected content-ingestion failure",
    stage: ContentIngestionStage.FinalizeSource,
    code: ContentIngestionErrorCode.Unknown,
    retryable: true,
    cause: error,
  });
}
